import React, { useState, useEffect, useRef, useMemo } from 'react';
import { kbOptimizer } from '../api';
import LoadingSpinner from './LoadingSpinner';
import AnimatedDropdown from './AnimatedDropdown';
import {
  Wand2, Upload, FileText, X, AlertTriangle, CheckCircle, Download, Eye, Scissors,
  Files, RotateCcw, Search, XCircle, Info, Sparkles, Cpu
} from 'lucide-react';

const JOB_STORAGE_KEY = 'kbOptimizer.lastJobId';
const ACCEPTED = ['txt', 'md', 'pdf'];

// Mirrors OPTIMIZER_MODELS in kb_optimizer_service.py; replaced by the API response on load
const FALLBACK_MODELS = [
  { id: 'claude-opus-5-5', label: 'Claude Opus 5.5', price_per_100kb: 1.31, price_per_100kb_pdf: 0.08 },
  { id: 'gpt-5.5', label: 'GPT-5.5', price_per_100kb: 1.40, price_per_100kb_pdf: 0.08 },
];

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const readStoredJob = () => {
  try { return localStorage.getItem(JOB_STORAGE_KEY); } catch { return null; }
};
const storeJob = (jobId) => {
  try {
    if (jobId) localStorage.setItem(JOB_STORAGE_KEY, jobId);
    else localStorage.removeItem(JOB_STORAGE_KEY);
  } catch { /* storage unavailable: results just won't survive a reload */ }
};

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const errorText = (error) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : (error?.message || 'Something went wrong');
};

const cardClass = 'bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant';
const inputClass = 'w-full px-4 py-3 bg-muted/30 border border-border/60 rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300';
const primaryBtn = 'px-6 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 font-medium btn-hover-gradient';
const secondaryBtn = 'px-4 py-2 rounded-lg text-sm font-medium border border-border/60 text-foreground hover:border-primary/40 hover:bg-muted/40 transition-all duration-300 flex items-center gap-2 disabled:opacity-50';

const Toggle = ({ checked, onChange, label, hint }) => (
  <label className="flex items-start gap-3 cursor-pointer select-none">
    <input type="checkbox" className="mt-1 w-4 h-4 accent-[hsl(var(--primary))]" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span>
      <span className="block text-sm font-medium text-foreground">{label}</span>
      {hint && <span className="block text-xs text-muted-foreground mt-0.5">{hint}</span>}
    </span>
  </label>
);

const KbOptimizer = () => {
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');

  const [sourceName, setSourceName] = useState('');
  const [outputName, setOutputName] = useState('');
  const [combine, setCombine] = useState(false);
  const [generateFaqs, setGenerateFaqs] = useState(true);
  const [splitMode, setSplitMode] = useState('auto');
  const [models, setModels] = useState(FALLBACK_MODELS);
  const [model, setModel] = useState('claude-opus-5-5');

  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewFilter, setPreviewFilter] = useState('');
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [confirmingCost, setConfirmingCost] = useState(false);
  const [extracting, setExtracting] = useState('');

  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  // Restore the last job (results are kept on the server for 2 hours)
  useEffect(() => {
    const stored = readStoredJob();
    if (stored) setJobId(stored);
    kbOptimizer.getModels()
      .then((response) => {
        if (response.data?.models?.length) setModels(response.data.models);
        if (response.data?.default) setModel(response.data.default);
      })
      .catch(() => { /* keep the built-in list */ });
    return () => clearTimeout(pollRef.current);
  }, []);

  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const response = await kbOptimizer.getJob(jobId);
        if (cancelled) return;
        setJob(response.data);
        if (response.data.status === 'queued' || response.data.status === 'running') {
          pollRef.current = setTimeout(poll, 1500);
        } else if (response.data.status === 'completed' && response.data.outputs?.length) {
          setSelectedFile((current) => current || response.data.outputs[0].filename);
        }
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          storeJob(null);
          setJobId(null);
          setJob(null);
        } else {
          pollRef.current = setTimeout(poll, 4000);
        }
      }
    };
    poll();
    return () => { cancelled = true; clearTimeout(pollRef.current); };
  }, [jobId]);

  // A cost confirmation only applies to the files and model it was shown for
  useEffect(() => { setConfirmingCost(false); }, [analysis, model]);

  const runAnalysis = async (list) => {
    setAnalysis(null);
    setError('');
    if (!list.length) return;
    setAnalyzing(true);
    try {
      const formData = new FormData();
      list.forEach((f) => formData.append('files', f));
      const response = await kbOptimizer.analyze(formData);
      setAnalysis(response.data);
      setSplitMode('auto');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setAnalyzing(false);
    }
  };

  const addFiles = (incoming) => {
    const rejected = [];
    const next = [...files];
    Array.from(incoming).forEach((f) => {
      const ext = f.name.split('.').pop().toLowerCase();
      if (!ACCEPTED.includes(ext)) { rejected.push(f.name); return; }
      if (next.some((x) => x.name === f.name && x.size === f.size)) return;
      next.push(f);
    });
    if (next.length > 10) {
      setError('You can optimize up to 10 files at a time.');
      return;
    }
    setFiles(next);
    if (rejected.length) setError(`Skipped ${rejected.join(', ')}: only .txt, .md and .pdf files are supported.`);
    runAnalysis(next);
  };

  const removeFile = (name) => {
    const next = files.filter((f) => f.name !== name);
    setFiles(next);
    runAnalysis(next);
  };

  const startOver = () => {
    clearTimeout(pollRef.current);
    storeJob(null);
    setJobId(null);
    setJob(null);
    setSelectedFile(null);
    setFiles([]);
    setAnalysis(null);
    setError('');
    setPreviewFilter('');
  };

  const startJob = async () => {
    // Large runs need an explicit yes; the API enforces the same threshold
    if (needsCostConfirm && !confirmingCost) {
      setConfirmingCost(true);
      return;
    }
    setConfirmingCost(false);
    setStarting(true);
    setError('');
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f));
      formData.append('options', JSON.stringify({
        split: splitMode,
        combine: combine && files.length > 1,
        source_name: sourceName.trim(),
        output_name: outputName.trim(),
        generate_faqs: generateFaqs,
        model,
        confirm_cost: needsCostConfirm,
      }));
      const response = await kbOptimizer.startJob(formData);
      setJob({ status: 'queued', stage: 'Queued', progress: 0 });
      setSelectedFile(null);
      storeJob(response.data.job_id);
      setJobId(response.data.job_id);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setStarting(false);
    }
  };

  const downloadZip = async () => {
    setDownloadingZip(true);
    try {
      const response = await kbOptimizer.downloadAll(jobId);
      saveBlob(response.data, `optimized_kb_${jobId.slice(0, 8)}.zip`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setDownloadingZip(false);
    }
  };

  const downloadExtracted = async (file) => {
    setExtracting(file.name);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await kbOptimizer.extractText(formData);
      saveBlob(response.data, `${file.name.replace(/\.[^.]+$/, '')}_extracted.txt`);
    } catch (e) {
      // Blob error bodies need decoding before the API message is readable
      let message = errorText(e);
      try { message = JSON.parse(await e.response.data.text()).detail || message; } catch { /* keep generic */ }
      setError(message);
    } finally {
      setExtracting('');
    }
  };

  const downloadOne = (output) => {
    saveBlob(new Blob([output.content], { type: 'text/plain;charset=utf-8' }), output.filename);
  };

  const okFiles = analysis?.files?.filter((f) => f.ok) || [];
  const badFiles = analysis?.files?.filter((f) => !f.ok) || [];
  const largeFiles = okFiles.filter((f) => f.is_large);
  const combinedTokens = okFiles.reduce((sum, f) => sum + (f.estimated_output_tokens || 0), 0);
  const combinedParts = Math.max(1, Math.ceil(combinedTokens / 25000));
  const showSplitAdvice = combine && files.length > 1 ? combinedParts > 1 : largeFiles.length > 0;
  const canStart = files.length > 0 && analysis && !badFiles.length && analysis.within_limits && !analyzing && !starting;

  const selectedModel = models.find((m) => m.id === model) || models[0];
  // Estimate from the extracted, cleaned text: PDF byte size is mostly images and says nothing about cost
  const estimatedCost = analysis?.estimates?.[model] ?? 0;
  const confirmAbove = analysis?.confirm_above_usd ?? 5;
  const needsCostConfirm = estimatedCost > confirmAbove;

  const isRunning = job && (job.status === 'queued' || job.status === 'running');
  const outputs = job?.status === 'completed' ? job.outputs || [] : [];
  const current = outputs.find((o) => o.filename === selectedFile) || outputs[0];

  const coverage = useMemo(() => {
    const bySource = {};
    outputs.forEach((o) => { if (o.coverage) bySource[o.source_file] = o.coverage; });
    return bySource;
  }, [outputs]);

  const previewBlocks = useMemo(() => {
    if (!current) return [];
    const blocks = current.content.split(/\n(?======)/);
    const q = previewFilter.trim().toLowerCase();
    return q ? blocks.filter((b) => b.toLowerCase().includes(q)) : blocks;
  }, [current, previewFilter]);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
            <Wand2 className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-1">KB Files Optimizer</h1>
            <p className="text-muted-foreground">Turn rough .txt or .md files into clean, structured knowledge base files your agents retrieve accurately.</p>
          </div>
        </div>
        {(job || files.length > 0) && (
          <button onClick={startOver} className={secondaryBtn} disabled={isRunning}>
            <RotateCcw className="w-4 h-4" /> Start over
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-3 p-4 rounded-lg border border-destructive/30 bg-destructive/10 text-destructive">
          <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">{error}</div>
          <button onClick={() => setError('')} aria-label="Dismiss"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Step 1 + 2: upload, analysis, options */}
      {!job && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
          <div className={cardClass}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                <Upload className="w-5 h-5 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">Source files</h3>
            </div>

            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
              onClick={() => fileInputRef.current?.click()}
              className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-all duration-300 ${dragOver ? 'border-primary bg-primary/10' : 'border-border/70 hover:border-primary/50 hover:bg-muted/30'}`}
            >
              <FileText className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <p className="font-medium text-foreground">Drop .txt, .md or .pdf files here, or click to browse</p>
              <p className="text-xs text-muted-foreground mt-1">Up to 10 files. Text files up to 5 MB; PDFs up to 50 MB and 100 pages. Messy, scraped or copy-pasted text is fine.</p>
              <p className="text-xs text-muted-foreground mt-1">PDFs are converted to text before any AI step. Images are ignored, and scanned PDFs need OCR first.</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".txt,.md,.pdf"
                className="hidden"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              />
            </div>

            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                {files.map((f) => {
                  const report = analysis?.files?.find((r) => r.filename === f.name);
                  return (
                    <div key={f.name} className="bg-background/30 border border-border/60 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">{f.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {formatBytes(f.size)}
                            {report?.pdf && ` · ${report.pdf.text_pages} of ${report.pdf.pages} pages with text`}
                            {report?.ok && ` · ~${report.cleaned_tokens.toLocaleString()} tokens after cleanup`}
                          </div>
                        </div>
                        {report && !report.ok && <span className="text-xs text-destructive">{report.error}</span>}
                        {report?.is_large && <span className="text-xs px-2 py-0.5 rounded-full bg-warning/20 text-warning">Large</span>}
                        {report?.ok && (
                          <button
                            onClick={() => downloadExtracted(f)}
                            disabled={extracting === f.name}
                            className="text-muted-foreground hover:text-primary disabled:opacity-50"
                            title="Download the cleaned text the AI will receive"
                          >
                            {extracting === f.name ? <LoadingSpinner size="sm" /> : <Download className="w-4 h-4" />}
                          </button>
                        )}
                        <button onClick={() => removeFile(f.name)} className="text-muted-foreground hover:text-destructive" aria-label={`Remove ${f.name}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      {report?.ok && report.cleanup && (
                        <div className="text-xs text-muted-foreground mt-2 pl-7">
                          {report.pdf && (
                            <div>
                              PDF: {report.pdf.header_footer_lines} header/footer lines, {report.pdf.page_number_lines} page numbers, {report.pdf.toc_lines} table-of-contents lines removed; {report.pdf.joined_hyphenations} split words rejoined. Images ignored.
                            </div>
                          )}
                          Pre-clean: {report.cleanup.duplicate_paragraphs} duplicate paragraphs, {report.cleanup.boilerplate_lines} repeated menu/footer lines, {report.cleanup.html_tags} HTML tags, {report.cleanup.rule_lines + report.cleanup.page_markers} divider/page lines removed.
                          {report.warnings?.filter((w) => !w.startsWith('This file is large')).map((w) => <div key={w}>{w}</div>)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {analyzing && <div className="mt-4"><LoadingSpinner size="sm" text="Checking files..." /></div>}
          </div>

          <div className={`${cardClass} flex flex-col`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                <Sparkles className="w-5 h-5 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">Options</h3>
            </div>

            <div className="space-y-4 flex-1">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  <Cpu className="w-4 h-4" />
                  AI model
                </label>
                <AnimatedDropdown
                  value={model}
                  onChange={(value) => setModel(value)}
                  options={models.map((m) => ({ value: m.id, label: `${m.label} (~$${m.price_per_100kb.toFixed(2)} per 100 KB text, ~$${(m.price_per_100kb_pdf ?? 0).toFixed(2)} per 100 KB PDF)` }))}
                  placeholder="Select a model"
                  icon={Cpu}
                />
                {files.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Estimated cost for these files: ~${estimatedCost < 0.01 ? '0.01' : estimatedCost.toFixed(2)}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Brand or source name (optional)</label>
                <input className={inputClass} value={sourceName} onChange={(e) => setSourceName(e.target.value)} placeholder="Detected automatically, e.g. City Cruises" />
                <p className="text-xs text-muted-foreground mt-1">Prefixed to every topic so each chunk names who it is about.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Output file name (optional)</label>
                <input className={inputClass} value={outputName} onChange={(e) => setOutputName(e.target.value)} placeholder="Defaults to the source file name" />
              </div>
              {files.length > 1 && (
                <Toggle checked={combine} onChange={setCombine} label="Combine all files into one knowledge base"
                  hint="Use when the files describe the same business. Otherwise each file is optimized on its own." />
              )}
              <Toggle checked={generateFaqs} onChange={setGenerateFaqs} label="Add likely customer questions"
                hint="Adds a few Q and A pairs per topic, answered only from facts in your file. Improves matching on spoken questions." />

              {showSplitAdvice && (
                <div className="rounded-lg border border-warning/40 bg-warning/10 p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-foreground space-y-2">
                      <p className="font-semibold">Large source detected</p>
                      {combine && files.length > 1 ? (
                        <p>Combined, these files come to about {combinedTokens.toLocaleString()} tokens of structured output. We recommend splitting the result into about {combinedParts} files.</p>
                      ) : largeFiles.map((f) => (
                        <p key={f.filename}><span className="font-medium">{f.filename}</span>: about {f.cleaned_tokens.toLocaleString()} tokens. We recommend about {f.recommended_files} output files.</p>
                      ))}
                      <p className="text-muted-foreground text-xs">Splitting groups related topics by category into files of roughly 100 KB. Retrieval quality is the same either way; smaller files are easier to review, update and replace one area at a time.</p>
                      <div className="flex flex-col gap-2 pt-1">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="split" checked={splitMode === 'auto'} onChange={() => setSplitMode('auto')} />
                          <Scissors className="w-4 h-4" /> Split into multiple files by category (recommended)
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="split" checked={splitMode === 'single'} onChange={() => setSplitMode('single')} />
                          <FileText className="w-4 h-4" /> Keep as a single file
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {analysis && !analysis.within_limits && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  These files total about {analysis.total_cleaned_tokens.toLocaleString()} tokens. The limit per run is {analysis.max_total_tokens.toLocaleString()}. Remove some files and run them in a second batch.
                </div>
              )}
            </div>

            {confirmingCost && (
              <div className="mt-6 rounded-lg border border-warning/40 bg-warning/10 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-foreground">
                    <p className="font-semibold">This run will cost about ${estimatedCost.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      That is above the ${confirmAbove.toFixed(0)} check. To spend less, download the extracted text with the download icon next to each file, delete what the agent does not need, and upload the trimmed .txt instead.
                    </p>
                    <div className="flex gap-2 mt-3">
                      <button onClick={startJob} className={secondaryBtn}>Continue (~${estimatedCost.toFixed(2)})</button>
                      <button onClick={() => setConfirmingCost(false)} className={secondaryBtn}>Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button onClick={startJob} disabled={!canStart || confirmingCost} className={`${primaryBtn} w-full mt-6`}>
              {starting ? <><LoadingSpinner size="sm" /> Starting...</> : <><Wand2 className="w-4 h-4" /> Optimize {files.length > 1 ? `${files.length} files` : 'file'}</>}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: progress */}
      {job && isRunning && (
        <div className={`${cardClass} max-w-2xl`}>
          <div className="flex items-center gap-3 mb-4">
            <LoadingSpinner size="sm" />
            <h3 className="text-lg font-semibold text-foreground">Optimizing your knowledge base</h3>
          </div>
          <div className="w-full h-2 rounded-full bg-muted/50 overflow-hidden">
            <div className="h-full bg-gradient-button transition-all duration-500" style={{ width: `${Math.max(3, job.progress || 0)}%` }} />
          </div>
          <div className="flex justify-between text-sm text-muted-foreground mt-2">
            <span>{job.stage}</span>
            <span>{job.progress || 0}%</span>
          </div>
          <p className="text-xs text-muted-foreground mt-4">Cleaning, organizing into topics, checking that prices, links and contact details survived, then formatting for retrieval. Large files take a few minutes. You can leave this page and come back.</p>
        </div>
      )}

      {job?.status === 'failed' && (
        <div className={`${cardClass} max-w-2xl`}>
          <div className="flex items-start gap-3">
            <XCircle className="w-6 h-6 text-destructive flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-foreground">Optimization failed</h3>
              <p className="text-sm text-muted-foreground mt-1">{job.error}</p>
              <button onClick={startOver} className={`${secondaryBtn} mt-4`}><RotateCcw className="w-4 h-4" /> Try again</button>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: results, preview, download */}
      {outputs.length > 0 && (
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6 min-h-0">
          <div className="space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 220px)' }}>
            <div className={cardClass}>
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-5 h-5 text-success" />
                <h3 className="text-lg font-semibold text-foreground">{outputs.length} file{outputs.length > 1 ? 's' : ''} ready</h3>
              </div>
              {job.usage && (
                <p className="text-xs text-muted-foreground mb-4">
                  Finished in {job.usage.seconds}s using {job.usage.model}
                  {typeof job.usage.cost_usd === 'number' && ` · AI cost ~$${job.usage.cost_usd.toFixed(2)}`}.
                </p>
              )}
              <button onClick={downloadZip} disabled={downloadingZip} className={`${primaryBtn} w-full`}>
                {downloadingZip ? <LoadingSpinner size="sm" /> : <Download className="w-4 h-4" />}
                {outputs.length > 1 ? 'Download all (.zip)' : 'Download (.zip)'}
              </button>
              <p className="text-xs text-muted-foreground mt-3">Upload the .txt files on the Knowledge Base page and assign them to your agent.</p>
            </div>

            {outputs.map((o) => (
              <div
                key={o.filename}
                onClick={() => { setSelectedFile(o.filename); setPreviewFilter(''); }}
                className={`cursor-pointer rounded-xl border p-4 transition-all duration-300 ${current?.filename === o.filename ? 'border-primary bg-primary/10' : 'border-border/60 bg-card/95 hover:border-primary/40'}`}
              >
                <div className="flex items-start gap-3">
                  <Files className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground break-all">{o.filename}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatBytes(o.bytes)} · {o.topics} topics · ~{o.estimated_chunks} chunks
                    </div>
                    <div className="text-xs text-muted-foreground truncate" title={o.categories.join(', ')}>{o.categories.join(', ')}</div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); downloadOne(o); }}
                    className="text-muted-foreground hover:text-primary"
                    title="Download this file"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}

            {Object.entries(coverage).map(([source, cov]) => (
              <div key={source} className={cardClass}>
                <div className="flex items-center gap-2 mb-2">
                  {cov.missing_count ? <AlertTriangle className="w-4 h-4 text-warning" /> : <CheckCircle className="w-4 h-4 text-success" />}
                  <h4 className="text-sm font-semibold text-foreground">Detail check: {source}</h4>
                </div>
                <p className="text-xs text-muted-foreground">
                  Checked {cov.checked} prices, percentages, links, emails and phone numbers against the output.
                  {cov.recovered_topics > 0 && ` Recovered ${cov.recovered_topics} topic${cov.recovered_topics > 1 ? 's' : ''} for details the first pass missed.`}
                </p>
                {cov.missing_count > 0 ? (
                  <>
                    <p className="text-xs text-foreground mt-2">Review these {cov.missing_count} details that are not in the output. They are often duplicates, worked examples or page chrome, but check any that matter:</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {cov.missing.map((m) => (
                        <button
                          key={`${m.kind}:${m.value}`}
                          onClick={() => setPreviewFilter(m.value)}
                          className="text-xs px-2 py-0.5 rounded-full bg-warning/15 text-foreground border border-warning/30 hover:border-warning break-all"
                          title={m.kind}
                        >
                          {m.value}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-success mt-2">Every detail found in the source is present in the output.</p>
                )}
              </div>
            ))}
          </div>

          {current && (
            <div className={`${cardClass} flex flex-col min-h-0`} style={{ maxHeight: 'calc(100vh - 220px)' }}>
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <Eye className="w-5 h-5 text-primary flex-shrink-0" />
                  <h3 className="text-lg font-semibold text-foreground truncate">{current.filename}</h3>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    value={previewFilter}
                    onChange={(e) => setPreviewFilter(e.target.value)}
                    placeholder="Filter topics"
                    className="w-full pl-9 pr-3 py-2 text-sm bg-muted/30 border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                <Info className="w-3.5 h-3.5" />
                Largest topic is {current.largest_topic_tokens} tokens (the retriever chunks at 400). {previewFilter && `${previewBlocks.length} matching topic${previewBlocks.length === 1 ? '' : 's'}.`}
              </div>
              <pre className="flex-1 overflow-auto text-xs leading-relaxed font-mono whitespace-pre-wrap break-words bg-background/50 border border-border/60 rounded-lg p-4 text-foreground">
                {previewBlocks.length ? previewBlocks.join('\n') : 'No topics match this filter.'}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KbOptimizer;
